import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'antd';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { register, registerReceiver } from '../actions/user';
import * as globalActions from '../actions/app/global';
import validationMessages from '../constants/ValidationMessages.json';
import { UserRole } from '../../types/user';
import { privacyUrl, termsUrl } from '../constants/ParamountReactConstants';
import ThreeLevelDropdown from '../components/global/ThreeLevelDropdown';
import brandImage from '../../images/icons/register-brand.svg';
import resellerImage from '../../images/icons/register-reseller.svg';
import emailImage from '../../images/icons/register-email.svg';
import LoginFooter from './LoginFooter';
import FormPasswordInput from '../components/global/Forms/FormPasswordInput';
import FormInput from '../components/global/Forms/FormInput';
import { extractErrorMessage } from '../utils/errorUtils';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const registrationResources = useSelector((state: any) => state.app.global.registrationResources);
  const fetchingRegistrationResources = useSelector(
    (state: any) => state.app.global.fetchingRegistrationResources
  );

  const [showRegisterFor, setShowRegisterFor] = useState<UserRole | undefined>(undefined);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registrationSuccessful, setRegistrationSuccessful] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (location.pathname === '/register/brand') setShowRegisterFor(UserRole.MANUFACTURER);
    if (location.pathname === '/register/reseller') setShowRegisterFor(UserRole.RECEIVER);

    if (!registrationResources) {
      dispatch(globalActions.fetchRegistrationResources());
    }
  }, [registrationResources, dispatch]);

  const CommonSchemaValidation = {
    firstName: Yup.string()
      .min(2, validationMessages.tooShort)
      .max(50, validationMessages.tooLong)
      .trim(validationMessages.leadingWhitespace)
      .strict()
      .matches(/^[^0-9]+$/, validationMessages.validateNoNumbers)
      .required(validationMessages.required),
    lastName: Yup.string()
      .min(2, validationMessages.tooShort)
      .max(50, validationMessages.tooLong)
      .trim(validationMessages.leadingWhitespace)
      .strict()
      .matches(/^[^0-9]+$/, validationMessages.validateNoNumbers)
      .required(validationMessages.required),
    email: Yup.string()
      .email(validationMessages.invalidEmail)
      .required(validationMessages.required),
    password: Yup.string()
      .required(t('validation:required'))
      .min(12, t('validation:passwordLength', { length: 12 }))
      .matches(/[0-9]/, t('validation:passwordRequiresNumber'))
      .matches(/[a-z]/, t('validation:passwordRequiresLowercase'))
      .matches(/[A-Z]/, t('validation:passwordRequiresUppercase'))
      .matches(/[^\w]/, t('validation:passwordRequiresSpecial')),
  };

  const RegisterReceiverSchema = Yup.object().shape({
    receiverName: Yup.string()
      .min(2, validationMessages.tooShort)
      .max(50, validationMessages.tooLong)
      .trim(validationMessages.leadingWhitespace)
      .strict()
      .required(validationMessages.required),
    ...CommonSchemaValidation,
  });

  const RegisterManufacturerSchema = Yup.object().shape({
    parentId: Yup.number().when(['parentName'], ([otherCompany], schema: Yup.NumberSchema) =>
      !otherCompany ? schema.required(validationMessages.required) : schema
    ),
    parentName: Yup.string(),
    ...CommonSchemaValidation,
  });

  const registerIntercomSignup = (
    firstName: string,
    lastName: string,
    email: string,
    otherCompany: string,
    parentId: number | string
  ) => {
    if (process.env.REACT_APP_STAGE && !email.includes('@pdm.ai')) {
      const name = `${firstName} ${lastName}`;
      const seconds = new Date().getTime() / 1000;
      const company = {
        id: parentId,
        name:
          registrationResources.find((reg: any) => reg.id === parentId)?.parent_name ||
          otherCompany,
      };
      if ((window as any).Intercom)
        (window as any).Intercom('update', {
          name,
          email,
          signed_up_at: seconds,
          company,
          company_name_at_signup: otherCompany, // custom field defined in intercom
          account_type: showRegisterFor === UserRole.RECEIVER ? 'Receiver' : 'Brand',
        });
    }
  };

  const errorMessage = () => {
    if (error) {
      return <Alert className="register__error-message" type="error" showIcon message={error} />;
    }
    return null;
  };

  const successBox = () => {
    return (
      <div className="register__success-box">
        <div className="register__success">
          <img src={emailImage} alt={t('common:email')} />
          <div className="login__title">{t('registerPage:registration_successful')}</div>
          <div className="mt-3 text-gray-700 text-xs">{t('registerPage:confirm_email')}</div>
          <div className="mt-3">{registeredEmail}</div>
          {/* todo: add button when backend endpoint is ready */}
          {/* <div className="mt-7">
            <Button>{t('registerPage:resend_email')}</Button>
          </div> */}
        </div>
      </div>
    );
  };

  const registerInfo = () => {
    return (
      <div className="text-xs text-center text-gray-700">
        {`${t('registerPage:sign_up_info')} `}
        <a className="register__link" href={termsUrl} target="_blank" rel="noopener noreferrer">
          {t('registerPage:terms')}
        </a>
        {` ${t('common:and')} `}
        <a className="register__link" href={privacyUrl} target="_blank" rel="noopener noreferrer">
          {t('registerPage:policy')}
        </a>
      </div>
    );
  };
  const receiverMethods = useForm({
    defaultValues: {
      receiverName: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    resolver: yupResolver(RegisterReceiverSchema),
    mode: 'onTouched',
  });

  const manufacturerMethods = useForm({
    defaultValues: {
      parentId: undefined,
      parentName: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    } as any,
    resolver: yupResolver(RegisterManufacturerSchema),
    mode: 'onTouched',
  });

  const registerBoxReceiver = () => {
    const { handleSubmit, formState } = receiverMethods;

    const onSubmit = async (values: any) => {
      setError(null);
      try {
        const result = (await dispatch(registerReceiver(values))) as any;
        const receiver = result.value.data;
        registerIntercomSignup(
          values.firstName,
          values.lastName,
          values.email,
          values.receiverName,
          receiver.receiver_id ? `r${receiver.receiver_id}` : ''
        );
        setRegistrationSuccessful(true);
        setRegisteredEmail(values.email);
      } catch (err: any) {
        setError(extractErrorMessage(err));
      }
    };

    return (
      <React.Fragment>
        {errorMessage()}
        <FormProvider {...receiverMethods}>
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <FormInput
              name="receiverName"
              label={t('registerPage:label.receiver_name')}
              placeholder={t('registerPage:placeholder.company')}
              required
              useHookForm
            />
            <Row gutter={20}>
              <Col span={12}>
                <FormInput
                  name="firstName"
                  label={t('registerPage:label.first_name')}
                  placeholder={t('registerPage:placeholder.first_name')}
                  required
                  useHookForm
                />
              </Col>
              <Col span={12}>
                <FormInput
                  name="lastName"
                  label={t('registerPage:label.last_name')}
                  placeholder={t('registerPage:placeholder.last_name')}
                  required
                  useHookForm
                />
              </Col>
            </Row>
            <FormInput
              name="email"
              label={t('registerPage:label.email')}
              placeholder={t('registerPage:placeholder.email')}
              type="email"
              required
              useHookForm
            />
            <FormPasswordInput
              className="register__password-form-item"
              name="password"
              label={t('registerPage:label.password')}
              placeholder={t('registerPage:label.password')}
              type="password"
              required
              useHookForm
            />
            <span className="text-xs whitespace-pre-line">{t('user:passwordInfo')}</span>

            <Button
              type="primary"
              htmlType="submit"
              disabled={!formState.isValid || formState.isSubmitting}
              style={{ width: '100%', marginTop: 20, marginBottom: 10 }}
            >
              {t('registerPage:sign_up')}
            </Button>
            {registerInfo()}
          </Form>
        </FormProvider>
      </React.Fragment>
    );
  };

  const registerBoxManufacturer = () => {
    const { handleSubmit, formState, control } = manufacturerMethods;

    const onSubmit = async (values: any) => {
      setError(null);
      try {
        setError(null);
        const result = await dispatch(register(values) as any);
        const manufacturer = result.value.data;
        registerIntercomSignup(
          values.firstName,
          values.lastName,
          values.email,
          values.parentName,
          manufacturer.parent_id || undefined
        );
        setRegistrationSuccessful(true);
        setRegisteredEmail(values.email);
      } catch (err: any) {
        const message = err.response?.data.message || err.message;
        setError(message);
      }
    };

    return (
      <React.Fragment>
        {errorMessage()}
        <FormProvider {...manufacturerMethods}>
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <Controller
              name="parentId"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label={t('registerPage:label.company')}
                  hasFeedback
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message || undefined}
                  required
                >
                  <ThreeLevelDropdown
                    elements={registrationResources}
                    selectedElement={field.value}
                    fetchingRegistrationResources={fetchingRegistrationResources}
                    onChange={(id?: number) => {
                      field.onChange(id);
                      manufacturerMethods.setValue('parentName', '');
                    }}
                    onBlur={field.onBlur}
                    addCompany={(name: string) => manufacturerMethods.setValue('parentName', name)}
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="parentName"
              control={control}
              render={({ field }) =>
                !manufacturerMethods.getValues('parentId') && field.value ? (
                  <FormInput
                    {...field}
                    name="parentName"
                    label={t('registerPage:label.other_company')}
                    useHookForm
                  />
                ) : (
                  <></>
                )
              }
            />
            <Row gutter={20}>
              <Col span={12}>
                <FormInput
                  name="firstName"
                  label={t('registerPage:label.first_name')}
                  placeholder={t('registerPage:placeholder.first_name')}
                  required
                  useHookForm
                />
              </Col>
              <Col span={12}>
                <FormInput
                  name="lastName"
                  label={t('registerPage:label.last_name')}
                  placeholder={t('registerPage:placeholder.last_name')}
                  required
                  useHookForm
                />
              </Col>
            </Row>
            <FormInput
              name="email"
              label={t('registerPage:label.email')}
              placeholder={t('registerPage:placeholder.email')}
              type="email"
              required
              useHookForm
            />
            <FormPasswordInput
              className="register__password-form-item"
              name="password"
              label={t('registerPage:label.password')}
              placeholder={t('registerPage:label.password')}
              type="password"
              required
              useHookForm
            />
            <span className="text-xs whitespace-pre-line">{t('user:passwordInfo')}</span>

            <Button
              type="primary"
              htmlType="submit"
              disabled={!formState.isValid || formState.isSubmitting}
              style={{ width: '100%', marginTop: 20, marginBottom: 10 }}
            >
              {t('registerPage:sign_up')}
            </Button>
            {registerInfo()}
          </Form>
        </FormProvider>
      </React.Fragment>
    );
  };

  return (
    <div className="register__background">
      <div className="register__box">
        <div className="register__box-header">
          <div className="register-logo" />
        </div>
        {!registrationSuccessful && (
          <div className="register__box-content">
            <div className="register--header-content">
              {!showRegisterFor && (
                <React.Fragment>
                  <div className="login__title">{t('registerPage:create_account')}</div>
                  <div className="mt-2">{t('registerPage:welcome_txt1')}</div>
                </React.Fragment>
              )}
              {showRegisterFor === UserRole.MANUFACTURER && (
                <React.Fragment>
                  <div className="login__title">{t('registerPage:create_brand_account')}</div>
                  <div className="mt-2">{t('registerPage:create_brand_info')}</div>
                </React.Fragment>
              )}
              {showRegisterFor === UserRole.RECEIVER && (
                <React.Fragment>
                  <div className="login__title">{t('registerPage:create_receiver_account')}</div>
                  <div className="mt-2">{t('registerPage:create_receiver_info')}</div>
                </React.Fragment>
              )}
            </div>
            {!showRegisterFor && (
              <div className="flex m-auto mt-5">
                <Button
                  className="register__large-button"
                  onClick={() => {
                    setShowRegisterFor(UserRole.MANUFACTURER);
                    navigate(`/register/brand`);
                  }}
                >
                  <img src={brandImage} alt={t('registerPage:manufacturer')} />
                  <div className="text-base">{t('registerPage:manufacturer')}</div>
                  <div className="text-xs mt-2">{t('registerPage:brand_info')}</div>
                </Button>
                <Button
                  className="register__large-button ml-5"
                  onClick={() => {
                    setShowRegisterFor(UserRole.RECEIVER);
                    navigate(`/register/reseller`);
                  }}
                >
                  <img src={resellerImage} alt={t('registerPage:receiver')} />
                  <div className="text-base">{t('registerPage:receiver')}</div>
                  <div className="text-xs mt-2">{t('registerPage:receiver_info')}</div>
                </Button>
              </div>
            )}
            {showRegisterFor && (
              <div className="register__box-content__form">
                {(showRegisterFor === UserRole.MANUFACTURER && registerBoxManufacturer()) ||
                  (showRegisterFor === UserRole.RECEIVER && registerBoxReceiver())}
              </div>
            )}
            <div className="register__login-container text-base">
              {`${t('registerPage:already_registered')} `}
              <Link className="register__login-text" to="/">
                {t('registerPage:login_txt')}
              </Link>
            </div>
          </div>
        )}
        {registrationSuccessful && successBox()}
      </div>
      <LoginFooter />
    </div>
  );
};

export default RegisterPage;
